import React, { useEffect } from "react";
import { BillingGuardrailsStatus, InvoiceNonpaymentStatus } from "../../../models/Entities";
import { fetchWithAuth } from "../../../api-client/APIClient";
import { Text, Alert, Button, ButtonVariant } from "@merge-api/merge-javascript-shared";
import { AlertTriangle, Info } from "lucide-react";
import { navigateToBillingPage } from "../../../router/RouterUtils";
import { useHistory } from "react-router-dom";
import { InvoiceNonpaymentBanner } from "./InvoiceNonpaymentBanner";

interface UserEventTrackerProps {
  event_name_input: string;
  impressions_input?: number;
  dismisses_input?: number;
  clicks_input?: number;
}

export function userEventTracker(props: UserEventTrackerProps) {
  const { event_name_input, impressions_input, dismisses_input, clicks_input } = props;
  fetchWithAuth({
    path: "/users/events",
    method: "PATCH",
    body: {
      event_name: event_name_input,
      impressions: impressions_input,
      dismisses: dismisses_input,
      clicks: clicks_input,
    },
    onResponse: () => {},
  });
}

interface BannerToShowProps {
  showAnyBanner: boolean;
  bannerStatus: boolean | boolean | BillingGuardrailsStatus | InvoiceNonpaymentStatus | null;
  showUpgradedBanner?: boolean;
  isFreePlan?: boolean;
  showApproachingLimitBanner?: boolean;
  showLimitReachedBanner?: boolean;
  numberOfCompletedAndIdleLinkedAccounts?: number;
  isInvoiceDueDateNull?: boolean | undefined;
  invoiceDueDate?: string;
  dismiss?: () => void;
  pricingBannerDismissed?: boolean;
  isPlaThresholdBannersEnabled?: boolean;
}

export const BannerToShow = ({
  showAnyBanner,
  bannerStatus,
  showUpgradedBanner,
  isFreePlan,
  showApproachingLimitBanner,
  showLimitReachedBanner,
  numberOfCompletedAndIdleLinkedAccounts,
  invoiceDueDate,
  isInvoiceDueDateNull,
  dismiss,
  pricingBannerDismissed,
  isPlaThresholdBannersEnabled,
}: BannerToShowProps) => {
  const history = useHistory();

  useEffect(() => {
    if (
      showAnyBanner &&
      bannerStatus == BillingGuardrailsStatus.BILLING_STATUS_FREE_AFTER_APRIL_2023
    ) {
      userEventTracker({
        event_name_input: "free_inform",
        impressions_input: 1,
        dismisses_input: 0,
        clicks_input: 0,
      });
    }
    if (showAnyBanner && showUpgradedBanner) {
      userEventTracker({
        event_name_input: "launch_upgrade",
        impressions_input: 1,
        dismisses_input: 0,
        clicks_input: 0,
      });
    }
    if (
      showAnyBanner &&
      bannerStatus ==
        BillingGuardrailsStatus.BILLING_STATUS_LAUNCH_AFTER_APRIL_2023_APPROACHING_CHARGE_LIMIT
    ) {
      userEventTracker({
        event_name_input: "launch_inform",
        impressions_input: 1,
        dismisses_input: 0,
        clicks_input: 0,
      });
    }
    if (
      showAnyBanner &&
      bannerStatus == BillingGuardrailsStatus.BILLING_STATUS_LAUNCH_AFTER_APRIL_2023_UPSELL_MOMENT
    ) {
      userEventTracker({
        event_name_input: "launch_upsell",
        impressions_input: 1,
        dismisses_input: 0,
        clicks_input: 0,
      });
    }
  }, []);

  return (
    <div className="flex-shrink-0">
      {!pricingBannerDismissed &&
        showLimitReachedBanner &&
        (isPlaThresholdBannersEnabled || isFreePlan) && (
          <Alert
            className="rounded-none z-10"
            color="red"
            showWarningIcon
            buttons={
              isFreePlan ? (
                <>
                  <Button size="sm" onClick={dismiss} variant={ButtonVariant.TextBlack}>
                    Dismiss
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => navigateToBillingPage(history)}
                    variant={ButtonVariant.TertiaryWhite}
                  >
                    Upgrade
                  </Button>
                </>
              ) : (
                <Button size="sm" onClick={dismiss} variant={ButtonVariant.TertiaryWhite}>
                  Acknowledge
                </Button>
              )
            }
          >
            <Text>
              <b>Linked Account limit reached</b>
              {isFreePlan ? (
                ". Upgrade your plan to sync all linked accounts."
              ) : (
                <>
                  {" "}
                  <b>and you will be charged for overages for new accounts.</b> Overages are
                  assessed at the 1st of every month for the prior month. Please delete any unused
                  Linked Accounts to avoid paying overages.
                </>
              )}
            </Text>
          </Alert>
        )}
      {!pricingBannerDismissed &&
        showApproachingLimitBanner &&
        (isPlaThresholdBannersEnabled || isFreePlan) && (
          <Alert
            className="rounded-none text-white z-10"
            color="black"
            icon={<Info size={16} />}
            buttons={
              isFreePlan ? (
                <>
                  <Button size="sm" onClick={dismiss} className="bg-transparent">
                    Dismiss
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => navigateToBillingPage(history)}
                    variant={ButtonVariant.TertiaryWhite}
                  >
                    Upgrade
                  </Button>
                </>
              ) : (
                <Button size="sm" onClick={dismiss} variant={ButtonVariant.TertiaryWhite}>
                  Acknowledge
                </Button>
              )
            }
          >
            <Text>
              {isFreePlan ? (
                <>
                  You are currently using{" "}
                  <b>
                    {numberOfCompletedAndIdleLinkedAccounts} production Linked Accounts of a limit
                    of 3
                  </b>
                  . Upgrade to link more accounts.
                </>
              ) : (
                <>
                  <b>90% of Linked Account limit utilized.</b> Overages will be charged for any
                  accounts after 100% utilization. Overages are assessed at the 1st of every month
                  for the prior month. Please delete any unused Linked Accounts to avoid paying
                  overages.
                </>
              )}
            </Text>
          </Alert>
        )}

      {showAnyBanner &&
        bannerStatus == InvoiceNonpaymentStatus.BILLING_STATUS_OVERDUE_INVOICE_FAILED_PAYMENT && (
          <InvoiceNonpaymentBanner showsIcon>
            There was an issue with your most recent payment. Please resolve the issue and try
            again. Your organization will be blocked from Merge's API{" "}
            <b>after a 30 day grace period.</b>
          </InvoiceNonpaymentBanner>
        )}
      {showAnyBanner &&
        bannerStatus ==
          InvoiceNonpaymentStatus.BILLING_STATUS_OVERDUE_INVOICE_MISSING_PAYMENT_METHOD && (
          <InvoiceNonpaymentBanner showsIcon>
            We are missing payment for your most recent invoice. Your organization will be blocked
            from Merge's API after a <b>30 day grace period</b>
          </InvoiceNonpaymentBanner>
        )}
      {showAnyBanner &&
        bannerStatus ==
          InvoiceNonpaymentStatus.BILLING_STATUS_OVERDUE_INVOICE_FAILED_PAYMENT_PAST_30 && (
          <InvoiceNonpaymentBanner showsIcon>
            There was an issue with your most recent payment. Please resolve the issue and try
            again. Your organization will be blocked from Merge's API until you resolve the issue
            and complete payment.
          </InvoiceNonpaymentBanner>
        )}
      {showAnyBanner &&
        isInvoiceDueDateNull &&
        bannerStatus ==
          InvoiceNonpaymentStatus.BILLING_STATUS_OVERDUE_INVOICE_MISSING_PAYMENT_METHOD_PAST_30 && (
          <InvoiceNonpaymentBanner showsIcon>
            We are missing payment for your most recent invoice. Your organization will be blocked
            from Merge's API until you resolve the issue and complete payment.
          </InvoiceNonpaymentBanner>
        )}
      {showAnyBanner &&
        !isInvoiceDueDateNull &&
        bannerStatus ==
          InvoiceNonpaymentStatus.BILLING_STATUS_OVERDUE_INVOICE_MISSING_PAYMENT_METHOD_PAST_30 && (
          <InvoiceNonpaymentBanner showsIcon>
            We are missing payment for your most recent invoice <b>{invoiceDueDate}</b>. Your
            organization will be blocked from Merge's API until you resolve the issue and complete
            payment.
          </InvoiceNonpaymentBanner>
        )}
    </div>
  );
};
